import React, { useEffect, useMemo, useState } from "react";
import ModernTable from "../../../../../components/ModernTable";
import { ColumnDef } from "@tanstack/react-table";
import useCampaignData, { CampaignItem } from "./useCampaign";
import Campaign from "../Campaign";
import { useNavigate } from "react-router-dom";
import { Edit, CalendarPlus, Eye, Upload } from "lucide-react";
import Modal from "../../../../../components/Model";
import ApiService from "../../../../../service/ApiService";
import { apiPaths } from "../../../../../service/apiPaths";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const CapmaignData = () => {
  const {
    campaigns,
    loading,
    error,
    creating,
    newCampaignName,
    setNewCampaignName,
    fetchCampaigns,
    createCampaign,
    selectedCampaignId,
    selectCampaign,
  } = useCampaignData();
  const navigate = useNavigate();

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [selectedImportCampaignId, setSelectedImportCampaignId] =
    useState<string>("");
  const [uploading, setUploading] = useState(false);
  const fileInputId = "campaign-data-import-input";

  

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewData, setViewData] = useState<CampaignItem | null>(null);

  // Schedule modal state
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleForm, setScheduleForm] = useState<any>({
    campaign: "",
    email: "",
    first_name: "",
    last_name: "",
    company_name: "",
    phone: "",
    website: "",
    lt_interest_status: 1,
    skip_if_in_workspace: true,
    skip_if_in_campaign: true,
    skip_if_in_list: true,
    personalization: "",
    custom_variables: {
      past_customer: false,
      follow_1: "",
      follow_2: "",
    },
  });

  const openSchedule = (campaignId: string) => {
    setScheduleForm((prev: any) => ({ ...prev, campaign: campaignId }));
    setIsScheduleOpen(true);
  };

  const closeSchedule = () => {
    setIsScheduleOpen(false);
  };

  const handleScheduleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target as any;
    if (name === "lt_interest_status") {
      setScheduleForm((prev: any) => ({ ...prev, [name]: Number(value) }));
      return;
    }
    if (
      name === "skip_if_in_workspace" ||
      name === "skip_if_in_campaign" ||
      name === "skip_if_in_list"
    ) {
      const checked = (e.target as HTMLInputElement).checked;
      setScheduleForm((prev: any) => ({ ...prev, [name]: checked }));
      return;
    }
    setScheduleForm((prev: any) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  const handleScheduleCustomVarChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, checked, type } = e.target;
    setScheduleForm((prev: any) => ({
      ...prev,
      custom_variables: {
        ...prev.custom_variables,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  const submitSchedule = async () => {
    if (!scheduleForm.campaign) {
      toast.error("Campaign is required");
      return;
    }
    if (!scheduleForm.email) {
      toast.error("Email is required");
      return;
    }
    try {
      setScheduleSubmitting(true);
      // await ApiService({
      //   method: "POST",
      //   endpoint: apiPaths.instantlyCampaignsSchedule,
      //   data: scheduleForm,
      // });
      toast.success("Lead scheduled successfully");
      setIsScheduleOpen(false);
      await fetchCampaigns();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || err?.message || "Failed to schedule"
      );
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const handleOpenImport = () => setIsImportOpen(true);
  const handleCloseImport = () => {
    setIsImportOpen(false);
    setSelectedImportCampaignId("");
  };

  const handleUploadCsvClick = () => {
    if (!selectedImportCampaignId) {
      toast.error("Please select a campaign");
      return;
    }
    const el = document.getElementById(fileInputId) as HTMLInputElement | null;
    el?.click();
  };


  const handleCsvSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      let uploadFile: File = file;

      const isExcelFile =
        /\.(xlsx|xls)$/i.test(file.name) ||
        file.type === "application/vnd.ms-excel" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      if (isExcelFile) {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const csvText = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csvText], { type: "text/csv" });
        const csvName = `${
          file.name.replace(/\.(xlsx|xls)$/i, "") || "import"
        }.csv`;
        uploadFile = new File([blob], csvName, { type: "text/csv" });
      }

      const formData = new FormData();
      formData.append("csvFile", uploadFile);
      formData.append("campaignId", selectedImportCampaignId);

      const resp = await ApiService({
        method: "POST",
        endpoint: apiPaths.campaignImportCsv,
        headers: { "Content-Type": "multipart/form-data" },
        data: formData,
      });
      const summary = resp?.summary || {};
      const total = summary?.totalRows ?? summary?.totalCount ?? 0;
      const success = summary?.successCount ?? 0;
      const errorCount = summary?.errorCount ?? (Array.isArray(resp?.errors) ? resp.errors.length : 0);
      const firstError = Array.isArray(resp?.errors) && resp.errors.length ? resp.errors[0] : null;

      if (errorCount > 0) {
        const details = firstError?.error
          ? ` Row ${firstError?.row ?? "?"}: ${firstError.error}`
          : "";
        toast.error(`Import completed with errors. ${success}/${total} succeeded, ${errorCount} failed.${details}`);
      } else if (total || success) {
        toast.success(`Import completed. ${success}/${total} rows imported.`);
      } else {
        toast.success("File imported successfully");
      }
      await fetchCampaigns();
      handleCloseImport();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || err?.message || "Import failed"
      );
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const columns = useMemo<ColumnDef<CampaignItem>[]>(
    () => [
      {
        accessorKey: "campaignName",
        header: "Campaign",
        size: 60,
        cell: ({ row }) => (
          <button
            type="button"
            className="text-left w-full text-blue-700 hover:underline"
            onClick={() =>
              navigate(`/campaign-list/${row.original.campaignId}`,{ state: { campaignName: row.original.campaignName } })
            }
          >
            {row.original.campaignName}
          </button>
        ),
      },
      {
        accessorKey: "leadCount",
        header: "Total Leads",
        size: 20,
        cell: ({ row }) => <span className="text-xs">{row.original.leadCount}</span>,
      },
      {
        accessorKey: "scheduled",
        header: "Scheduled",
        size: 20,
        cell: ({ row }) => (
          <button
            type="button"
            className="text-xs text-blue-700 hover:underline"
            onClick={() =>
              navigate(`/campaign-list/${row.original.campaignId}`, {
                state: { filterStatus: "Scheduled", campaignName: row.original.campaignName },
              })
            }
          >
            {row.original.scheduled}
          </button>
        ),
      },
      {
        accessorKey: "queryCount",
        header: "Queries",
        size: 20,
        cell: ({ row }) => (
          <button
            type="button"
            className="text-xs text-blue-700 hover:underline"
            onClick={() =>
              navigate(`/campaign-list/${row.original.campaignId}`, {
                state: { filterStatus: "Query", campaignName: row.original.campaignName },
              })
            }
          >
            {row.original.queryCount}
          </button>
        ),
      },
    ],
    [navigate]
  );

  const [bouncifyCredits, setBouncifyCredits] = useState<number | null>(null);
  const [bouncifyLoading, setBouncifyLoading] = useState<boolean>(false);
  const [bouncifyError, setBouncifyError] = useState<string | null>(null);

  useEffect(() => {
    const key =
      (process.env.REACT_APP_BOUNCIFY_API_KEY as string | undefined) ||
      (typeof window !== "undefined"
        ? (localStorage.getItem("BOUNCIFY_API_KEY") as string | null)
        : null);
    if (!key) return;
    const controller = new AbortController();
    console.log("Key Bouncify",key);
    console.log("Key controller",controller);
    const fetchCredits = async () => {
      try {
        setBouncifyLoading(true);
        setBouncifyError(null);
        const resp = await fetch(
          `https://api.bouncify.io/v1/info?apikey=${encodeURIComponent(key)}`,
          { signal: controller.signal }
        );
        const json: any = await resp.json().catch(() => ({}));
        const creditsRemaining =
          json?.credits_info?.credits_remaining ?? json?.credits ?? null;
        if (typeof creditsRemaining === "number") {
          setBouncifyCredits(creditsRemaining);
        } else {
          setBouncifyCredits(null);
        }
        console.log("Credit Bouncify",json);
      } catch (e: any) {
        setBouncifyError(e?.message || "Failed to load credits");
      } finally {
        setBouncifyLoading(false);
      }
    };
    fetchCredits();
    return () => controller.abort();
  }, []);

  const isLowCredits = typeof bouncifyCredits === "number" && bouncifyCredits < 100;


  return (
    <div className="m-5 flex flex-col gap-4 h-[80vh]">
      <div className="bg-white rounded-lg shadow p-4 flex  gap-2 justify-end">
        {/* <input
          type="text"
          placeholder='New campaign name'
          value={newCampaignName}
          onChange={(e) => setNewCampaignName(e.target.value)}
          className="border rounded px-3 py-1 text-sm w-64"
        /> */}
        <button
          type="button"
          onClick={handleOpenImport}
          className="px-3 py-1 bg-blue-600 text-white rounded text-sm"
        >
          Add New
        </button>
      </div>
      <div
              className={`text-xs px-2 w-40 py-1 rounded border ${
                isLowCredits
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-amber-50 border-amber-200 text-amber-700"
              }`}
              title={
                bouncifyError
                  ? bouncifyError
                  : "Bouncify account credits remaining"
              }
            >
              Email Credits: <span className="font-bold">{bouncifyLoading ? "..." : bouncifyCredits ?? "-"}</span>
              {isLowCredits && (
                <span className="ml-1 font-semibold">Increase limit</span>
              )}
            </div>

      <div className="h-[80vh]">
        <ModernTable<CampaignItem>
          data={campaigns}
          columns={columns}
          loading={loading}
          title="Campaigns"
          showSearch={false}
          showAddButton={false}
          showPagination={false}
          headerActions={null}
        />
        {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      </div>

      <Modal
        isOpen={isImportOpen}
        onClose={handleCloseImport}
        style="max-width: 28rem; width: 100%"
      >
        <h2 className="text-lg font-semibold mb-4">Select Campaign & Upload</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs mb-1">Campaign</label>
            <select
              className="w-full border rounded px-2 py-1"
              value={selectedImportCampaignId}
              onChange={(e) => setSelectedImportCampaignId(e.target.value)}
            >
              <option value="">Select campaign</option>
              {campaigns.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.campaignName}
                </option>
              ))}
            </select>
          </div>
          <div className="pt-1"></div>
          <input
            id={fileInputId}
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleCsvSelected}
          />
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleCloseImport}
              className="px-3 py-1 border rounded"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUploadCsvClick}
              disabled={uploading || !selectedImportCampaignId}
              className="flex items-center gap-2 px-3 py-1 bg-emerald-600 text-white rounded disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />{" "}
              {uploading ? "Importing..." : "Upload CSV"}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        style="max-width: 32rem; width: 100%"
      >
        <h2 className="text-lg font-semibold mb-4">Campaign Details</h2>
        <div className="grid grid-cols-2 gap-12 text-sm">
          <div>
            <div className="text-xs text-gray-500">Name</div>
            <div className="font-medium">{viewData?.campaignName || "-"}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Total</div>
            <div className="font-medium">{viewData?.leadCount ?? 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Scheduled</div>
            <div className="font-medium">{viewData?.scheduled ?? 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Queries</div>
            <div className="font-medium">{(viewData as any)?.queryCount ?? 0}</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={() => setIsViewOpen(false)}
            className="px-3 py-1 border rounded"
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CapmaignData;
